import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { State } from '../store';
import { selectAuthState } from '../store/selectors/auth.selectors';

/**
 * Admins whose access is limited to a specific set of sections.
 * Keyed by lowercased email; the value is the list of allowed router path prefixes.
 *
 * Used for restricted testers (e.g. staging). The account itself must exist in the
 * target backend for login to succeed; this map only limits what the frontend
 * exposes once such an admin is logged in.
 */
export const RESTRICTED_ADMINS: { readonly [email: string]: readonly string[] } = {
  // Staging tester: Users tab + Tools > Seed Users only.
  'adegtiarev@gmail.com': ['/users', '/tools/seed-users'],
};

@Injectable({ providedIn: 'root' })
export class AccessControlService {
  constructor(private store: Store<State>) {}

  /** Allowed path prefixes for the current admin, or null when unrestricted. */
  readonly allowedPaths$: Observable<readonly string[] | null> = this.store
    .select(selectAuthState)
    .pipe(
      map(authState => {
        const email = authState && authState.auth && authState.auth.user
          ? authState.auth.user.email
          : null;
        if (!email) {
          return null;
        }
        return RESTRICTED_ADMINS[email.trim().toLowerCase()] || null;
      }),
    );

  /** Whether the given router URL is reachable given the allowed prefixes. */
  isAllowed(allowedPaths: readonly string[] | null, url: string): boolean {
    if (!allowedPaths) {
      return true;
    }
    const rawPath = (url || '').split('?')[0].split('#').pop() || '';
    const path = ('/' + rawPath.replace(/^\/+/, '')).toLowerCase();
    if (path === '/' || path === '/login') {
      return true;
    }
    // Allow a section landing page (e.g. /tools) when any child of it is allowed.
    if (allowedPaths.some(prefix => prefix.startsWith(path + '/'))) {
      return true;
    }
    return allowedPaths.some(prefix => path === prefix || path.startsWith(prefix + '/'));
  }
}
